import {
  Button,
  Checkbox,
  Columns,
  Icon,
  Link,
  PasswordInput,
  TextInput,
  Typography,
} from "@fullscript/aviary-web";
import { faCircleExclamation } from "@fullscript/pro-regular-svg-icons";
import { Trans, useTranslation } from "react-i18next";

import { LoginOptions } from "@shared/components/LoginOptions/LoginOptions";
import { OrSection } from "@shared/components/LoginOptions/OrSection/OrSection";
import { sharedRoutes } from "@shared/data/sharedRoutes";
import { useUnauthUrlFields } from "@shared/hooks/useUnauthUrlFields/useUnauthUrlFields";
import { ErrorBox } from "@unauthenticated/shared/components/ErrorBox/ErrorBox";
import { PractitionerSignUpEmailInput } from "@unauthenticated/shared/components/PractitionerSignUpEmailInput/PracitionerSignUpEmailInput";
import { RecaptchaErrorBox } from "@unauthenticated/shared/components/RecaptchaErrorBox/RecaptchaErrorBox";
import { l } from "@unauthenticated/shared/locales/i18n";

import { useOidcSignUpForm } from "./useOidcSignUpForm";

import * as styles from "./OidcSignUpForm.styles";

interface Props {
  clientId: string;
  onSignUpComplete: () => void;
  onSignInRedirect: (email: string) => void;
}

const OidcSignUpForm = ({ clientId, onSignUpComplete, onSignInRedirect }: Props) => {
  const { t } = useTranslation();
  const utmFields = useUnauthUrlFields({ isCamelCaseKeys: true });

  const {
    currentSubStep,
    formData,
    errors,
    passwordCaption,
    stepAnnouncement,
    isLoading,
    submitError,
    passwordError,
    isAccountExists,
    setIsAccountExists,
    recaptchaError,
    setEmail,
    handleFieldChange,
    handleTosChange,
    handleFormSubmit,
  } = useOidcSignUpForm({ clientId, onSignUpComplete });

  const isOnStepTwo = currentSubStep === 2;
  const submitLabel = isOnStepTwo ? t(l.oauthSignUp.createAccount) : t(l.oauthSignUp.continue);
  const passwordFieldError = errors.password ?? passwordError ?? undefined;

  return (
    <>
      <div role="status" aria-live="polite" css={styles.srOnly}>
        {stepAnnouncement}
      </div>
      <form css={styles.form} onSubmit={handleFormSubmit} noValidate>
        <Columns isMarginless>
          <Columns.Column columnWidth={6}>
            <TextInput
              id="oidc-first-name"
              name="firstName"
              label={t(l.oauthSignUp.fields.firstName)}
              value={formData.firstName}
              onChange={handleFieldChange("firstName")}
              errorCaption={errors.firstName}
            />
          </Columns.Column>
          <Columns.Column columnWidth={6}>
            <TextInput
              id="oidc-last-name"
              name="lastName"
              label={t(l.oauthSignUp.fields.lastName)}
              value={formData.lastName}
              onChange={handleFieldChange("lastName")}
              errorCaption={errors.lastName}
            />
          </Columns.Column>
        </Columns>
        {!!isOnStepTwo && (
          <>
            {!!submitError && <ErrorBox error={submitError} />}
            {!!recaptchaError && <RecaptchaErrorBox />}
            <PractitionerSignUpEmailInput
              id="oidc-email"
              name="email"
              value={formData.email}
              setEmail={setEmail}
              isDirty={!!formData.email || !!errors.email}
              isAccountExists={isAccountExists}
              setIsAccountExists={setIsAccountExists}
              onSignInRedirect={() => onSignInRedirect(formData.email)}
              onChange={handleFieldChange("email")}
              required
            />
            <PasswordInput
              id="oidc-password"
              name="password"
              label={t(l.oauthSignUp.fields.password)}
              value={formData.password}
              onChange={handleFieldChange("password")}
              inputCaption={passwordCaption}
              errorCaption={passwordFieldError}
              required
            />
            <div css={styles.orSectionWrapper}>
              <OrSection />
            </div>
            <div css={styles.ssoButtons}>
              <LoginOptions
                hideOrText
                commonSSOProps={{
                  additionalAttributes: {
                    signupSource: "fs_academy",
                    apiClientId: clientId,
                    ...utmFields,
                  },
                }}
              />
            </div>
          </>
        )}
        <div css={styles.tosRow}>
          <Checkbox
            checked={!!formData.tosTimestamp}
            onChange={handleTosChange}
            aria-label={t(l.oauthSignUp.tosAriaLabel)}
          >
            <Trans
              i18nKey={l.oauthSignUp.tos}
              components={{
                tos: <Link href={sharedRoutes.external.termsOfService} hideExternalIcon />,
                privacy: <Link href={sharedRoutes.external.privacyPolicy} hideExternalIcon />,
              }}
            />
          </Checkbox>
          {!!errors.tos && (
            <div css={styles.tosError} role="alert">
              <Icon icon={faCircleExclamation} intention="critical" size="xsmall" />
              <Typography variant="footnote" isMarginless customCss={styles.tosErrorText}>
                {errors.tos}
              </Typography>
            </div>
          )}
        </div>
        <Button type="submit" isFullWidth customCss={styles.submit} disabled={isLoading}>
          {submitLabel}
        </Button>
      </form>
    </>
  );
};

export { OidcSignUpForm };
